// v3.15-1209 ADMIN-005 — Employee Career Dashboard (read-only projection).
//
// Displays an employee's current career progression from existing config,
// immutable records, and replayable projections. Never emits an event.
// Never evaluates a badge rule. Never calculates compensation.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, User, Trophy, Star, Medal, ShieldCheck, TrendingUp, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const fmtMoney = (n) => n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct   = (n) => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const fmtDate  = (iso) => (iso ? iso.slice(0, 10) : '—');

const iconFor = (tok, cls = 'w-4 h-4') => {
  switch (tok) {
    case 'trophy': return <Trophy className={cls} />;
    case 'star':   return <Star className={cls} />;
    case 'shield': return <ShieldCheck className={cls} />;
    default:       return <Medal className={cls} />;
  }
};

const isAdminRole = (r) => ['ptc_admin', 'company_director', 'manager_director'].includes((r || '').toLowerCase());

/* ============================== Widgets ============================== */

const ProgressBar = ({ pct, tone = 'amber' }) => {
  const width = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  const colors = {
    amber:   'bg-gradient-to-r from-amber-400 to-amber-600',
    emerald: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
    blue:    'bg-gradient-to-r from-blue-400 to-blue-600',
  };
  return (
    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden" data-testid="cd-progress-bar">
      <div className={`h-full ${colors[tone] || colors.amber} transition-all duration-500`} style={{ width: `${width}%` }} />
    </div>
  );
};

const CompensationCard = ({ plan }) => (
  <div data-testid={`cd-plan-${plan.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">Compensation Plan</div>
        <h3 className="text-lg font-extrabold text-slate-900">{plan.plan_name || plan.plan_code}</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {plan.role} · L{plan.level ?? 1} · v{plan.version || '—'} · Metric: {plan.production_metric?.replaceAll('_', ' ')}
        </p>
      </div>
      <TrendingUp className="w-6 h-6 text-blue-600" />
    </div>

    <div className="grid grid-cols-3 gap-3 mb-4">
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Current Production</div>
        <div className="text-xl font-extrabold text-slate-900" data-testid="cd-production">{fmtMoney(plan.current_production)}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">Period {plan.qualification_period}</div>
      </div>
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Current Tier</div>
        <div className="text-xl font-extrabold text-blue-700" data-testid="cd-current-tier">
          {plan.current_tier ? `${plan.current_tier.rate}%` : '—'}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          {plan.current_tier
            ? `${fmtMoney(plan.current_tier.min)}–${plan.current_tier.max == null ? '∞' : fmtMoney(plan.current_tier.max)}`
            : '—'}
        </div>
      </div>
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Next Tier</div>
        <div className="text-xl font-extrabold text-emerald-700" data-testid="cd-next-tier">
          {plan.next_tier ? `${plan.next_tier.rate}%` : '—'}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          {plan.next_tier ? `starts at ${fmtMoney(plan.next_tier.min)}` : (plan.top_tier_reached ? 'Top rung' : '—')}
        </div>
      </div>
    </div>

    <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
      <span>Progress to next tier</span>
      <span className="font-semibold text-slate-700" data-testid="cd-progress-pct">
        {plan.remaining_production != null ? `${fmtMoney(plan.remaining_production)} remaining` : '—'} · {fmtPct(plan.progress_pct)}
      </span>
    </div>
    <ProgressBar pct={plan.progress_pct} tone="blue" />

    {(plan.tiers || []).length > 0 && (
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Tier ladder</div>
        <div className="flex flex-wrap gap-2">
          {plan.tiers.map((t, i) => {
            const active = plan.current_tier && t.min === plan.current_tier.min && t.rate === plan.current_tier.rate;
            return (
              <span key={i}
                    data-testid={`cd-tier-${i}`}
                    className={
                      'text-[11px] px-2 py-1 rounded-full border ' +
                      (active
                        ? 'bg-blue-600 text-white border-blue-600 font-bold'
                        : 'bg-white text-slate-600 border-slate-200')
                    }>
                {t.rate}% · {fmtMoney(t.min)}{t.max ? `–${fmtMoney(t.max)}` : '+'}
                {t.badge ? ` · ${t.badge}` : ''}
              </span>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

const BadgesCard = ({ badges }) => {
  const nb = badges.next_badge;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="cd-badges-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Recognition</div>
          <h3 className="text-lg font-extrabold text-slate-900">{badges.program?.name || 'Badge Program'}</h3>
        </div>
        <Trophy className="w-6 h-6 text-amber-600" />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Current Badge</div>
          <div className="mt-1 flex items-center gap-2">
            {badges.current_badge
              ? <>
                  <span className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-amber-600">
                    {iconFor(badges.current_badge.icon)}
                  </span>
                  <div>
                    <div className="text-base font-extrabold text-slate-900" data-testid="cd-current-badge">
                      {badges.current_badge.order}. {badges.current_badge.name}
                    </div>
                    <div className="text-[10px] text-slate-500">Awarded {fmtDate(badges.current_badge.awarded_at)}</div>
                  </div>
                </>
              : <span className="text-sm text-slate-500" data-testid="cd-current-badge">— none yet —</span>}
          </div>
        </div>
        <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Next Badge</div>
          <div className="mt-1 flex items-center gap-2">
            {nb
              ? <>
                  <span className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-amber-600">
                    {iconFor(nb.icon)}
                  </span>
                  <div>
                    <div className="text-base font-extrabold text-slate-900" data-testid="cd-next-badge">
                      {nb.order}. {nb.name}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {nb.manager_approval_required   ? 'Mgr approval · '  : ''}
                      {nb.executive_approval_required ? 'Exec approval · ' : ''}
                      {(nb.remaining_requirements || []).length} rule{(nb.remaining_requirements || []).length === 1 ? '' : 's'}
                    </div>
                  </div>
                </>
              : <span className="text-sm text-slate-500" data-testid="cd-next-badge">— top rung reached —</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
        <span>Ladder progress ({badges.progress.achieved} / {badges.progress.total})</span>
        <span className="font-semibold text-slate-700">{fmtPct(badges.progress.pct)}</span>
      </div>
      <ProgressBar pct={badges.progress.pct} tone="amber" />

      {nb && (nb.remaining_requirements || []).length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Remaining requirements for {nb.name}
          </div>
          <div className="space-y-2">
            {nb.remaining_requirements.map((req, i) => (
              <div key={i} data-testid={`cd-next-req-${i}`}
                   className="p-2 border border-slate-100 rounded-lg bg-slate-50/60 text-xs">
                {Object.keys(req.criteria || {}).length === 0 ? (
                  <span className="text-slate-500">Manual review — no threshold criteria configured.</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(req.criteria).map(([k, v]) => (
                      <span key={k} className="inline-block bg-white border rounded px-2 py-0.5">
                        <span className="text-slate-500">{k.replaceAll('_', ' ')}:</span> <span className="font-semibold">{String(v)}</span>
                      </span>
                    ))}
                  </div>
                )}
                {(req.manager_approval_required || req.executive_approval_required) && (
                  <div className="text-[10px] text-amber-700 mt-1">
                    {req.manager_approval_required   ? '✓ Manager approval required. ' : ''}
                    {req.executive_approval_required ? '✓ Executive approval required.' : ''}
                  </div>
                )}
                {req.notes && <div className="text-[10px] text-slate-500 mt-1">{req.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(badges.ladder || []).length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Full ladder</div>
          <div className="flex flex-wrap gap-2">
            {badges.ladder.map((b) => (
              <span key={b.id} data-testid={`cd-ladder-${b.id}`}
                    className={
                      'inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ' +
                      (b.awarded
                        ? 'bg-amber-600 text-white border-amber-600 font-bold'
                        : 'bg-white text-slate-600 border-slate-200')
                    }>
                {iconFor(b.icon, 'w-3 h-3')}
                {b.order}. {b.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================== Page ============================== */

export default function CareerDashboardPage() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        setMe(data);
      } catch { /* fall back to unauth-visible /me */ }
    })();
  }, []);

  const isAdmin = useMemo(() => isAdminRole(me?.role), [me]);

  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  // Admins fetch the employee list once.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get('/users', { params: { limit: 500 } });
        const list = (data?.items || data || []).filter((u) => u.is_active !== false);
        setEmployees(list);
      } catch { /* non-fatal */ }
    })();
  }, [isAdmin]);

  const load = useCallback(async (empId) => {
    setLoading(true); setError(null);
    try {
      const url = empId ? `/career-dashboard/employee/${empId}` : '/career-dashboard/me';
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load dashboard');
      setData(null);
    } finally { setLoading(false); }
  }, []);

  // Initial load: admin without selection → self; non-admin → self.
  useEffect(() => {
    if (me === null) return;
    load(isAdmin && selectedEmp ? selectedEmp : null);
  }, [me, isAdmin, selectedEmp, load]);

  const emp = data?.employee;

  return (
    <div className="space-y-4" data-testid="career-dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <User className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Career Dashboard</h1>
            <p className="text-sm text-slate-500">
              Read-only projection of career progression. Sourced entirely from immutable records
              and replayable projections — nothing is calculated or evaluated here.
            </p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <select
              data-testid="cd-employee-select"
              value={selectedEmp}
              onChange={(e) => setSelectedEmp(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[260px]"
            >
              <option value="">— My own dashboard —</option>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading career projection…
        </div>
      ) : error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl" data-testid="cd-error">
          {error}
        </div>
      ) : data && emp ? (
        <>
          {/* Profile + roles + companies */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="cd-profile">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Employee</div>
                <h2 className="text-xl font-extrabold text-slate-900">{emp.full_name || emp.email}</h2>
                <p className="text-sm text-slate-500">{emp.email}</p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 justify-end">
                  <Building2 className="w-3.5 h-3.5" /> Current Company
                </div>
                <div className="text-base font-bold text-slate-800" data-testid="cd-company">{emp.company_name || '—'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4 border-t border-slate-100 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Primary role</div>
                <span className="inline-block text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-bold"
                      data-testid="cd-primary-role">
                  {emp.primary_role?.role || '—'} · L{emp.primary_role?.level || 1}
                </span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Additional roles</div>
                {(emp.additional_roles || []).length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {emp.additional_roles.map((r, i) => (
                      <span key={i} data-testid={`cd-additional-role-${i}`}
                            className={
                              'inline-block text-[11px] px-2 py-1 rounded-full ' +
                              (r.is_active
                                ? 'bg-emerald-100 text-emerald-700 font-semibold'
                                : 'bg-slate-100 text-slate-500 line-through')
                            }>
                        {r.role} · L{r.level || 1}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {(emp.additional_companies || []).length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Additional companies</div>
                <div className="flex flex-wrap gap-1.5">
                  {emp.additional_companies.map((c, i) => (
                    <span key={i} data-testid={`cd-additional-company-${i}`}
                          className="inline-block text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">
                      {c.company_id?.slice(0, 8)}… · {c.role} · L{c.level || 1}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Compensation projections */}
          {(data.compensation_plans || []).length === 0 ? (
            <div className="p-6 bg-white rounded-2xl border border-slate-200 text-slate-500 text-sm"
                 data-testid="cd-plans-empty">
              No compensation plans assigned to this employee.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {data.compensation_plans.map((p) => <CompensationCard key={p.id} plan={p} />)}
            </div>
          )}

          {/* Badges projection */}
          {data.badges?.program ? (
            <BadgesCard badges={data.badges} />
          ) : (
            <div className="p-6 bg-white rounded-2xl border border-slate-200 text-slate-500 text-sm"
                 data-testid="cd-badges-empty">
              No badge program configured for this employee&apos;s company.
            </div>
          )}
        </>
      ) : (
        <div className="p-10 text-center text-slate-500 bg-white rounded-2xl border">
          No data.
        </div>
      )}
    </div>
  );
}
